import { OverlayAnnotations } from '@readcloud/common';
import memo from 'nano-memoize';
import React from 'react';
import ContentEditable from 'react-contenteditable';
import sanitizeHtml from 'sanitize-html';
import { ScaleWithPageContainer } from '../ScaleWithPageContainer';
import { AnnotationRendererProps } from '../types';

import interact from 'interactjs';
const stop = (e: React.TouchEvent | React.MouseEvent | React.PointerEvent) => {
  e.stopPropagation();
};

const autoFocusRef = (el: HTMLElement | null) => el && el.focus();

const clickawayContainerStyle = {
  height: '100%',
  width: '100%',
  overflow: 'hidden',
} as const;

const voidfn = () => {};

const sanitizeHtmlCustom = memo((str: string) => sanitizeHtml(str));

export const OverlayTextAnnotationViewer = React.memo(
  function OverlayTextAnnotationViewer(
    props: AnnotationRendererProps<
      OverlayAnnotations.OverlayTextAnnotation['data'],
      OverlayAnnotations.OverlayTextAnnotation['type']
    > & {
      onTextChange: (text: string) => void;
    }
  ) {
    const [editingText, setEditingText] = React.useState(false);
    const annotationReference = React.useRef();
    const onContentChange = React.useCallback<
      React.ComponentProps<typeof ContentEditable>['onChange']
    >(
      (e) => {
        props.onTextChange(e.target.value);
      },
      [props.onTextChange]
    );
    const viewingStyle = React.useMemo(
      () => ({
        height: '100%',
        width: '100%',
        color: props.data.fill,
        fontFamily: props.data.fontFamily,
        fontSize: props.data.fontSize,
        fontWeight: props.data.fontWeight,
        wordBreak: props.data.wordBreak,
        overflowWrap: props.data.overflowWrap,
        textAlign: props.data.textAlign,
      }),
      [props.data]
    );
    const editingStyle = React.useMemo(
      () => ({
        cursor: 'text',
        height: '100%',
        width: '100%',
        color: props.data.fill,
        fontFamily: props.data.fontFamily,
        fontSize: props.data.fontSize,
        fontWeight: props.data.fontWeight,
        wordBreak: props.data.wordBreak,
        overflowWrap: props.data.overflowWrap,
        textAlign: props.data.textAlign,
      }),
      [props.data]
    );

    /*   const handleDoubleClick = React.useCallback((e: React.MouseEvent) => {
      setEditingText(true);
    }, []); */

    const onCancelEdit = React.useCallback(() => {
      setEditingText(false);
    }, []);

    const sanitizedHtml = React.useMemo(
      () => sanitizeHtmlCustom(props.data.text),
      [props.data.text]
    );

    const tappableRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      if (tappableRef && props.selected) {
        const listener: Interact.Listener = (e) => {
          setEditingText(true);
        };
        const interaction = interact(tappableRef.current);
        setTimeout(() => {
          interaction.on('tap', listener);
        }, 150);

        return () => {
          interaction.off('tap', listener);
        };
      }
      return;
    }, [tappableRef, props.selected]);

    return (
      <ScaleWithPageContainer
        height={props.height}
        width={props.width}
        scale={props.scale}
        scaleWithPage={!!props.data.scaleWithPage}
      >
        <div
          tabIndex={0}
          onMouseDown={editingText ? stop : undefined}
          onTouchStart={editingText ? stop : undefined}
          //onDoubleClick={props.editing ? handleDoubleClick : undefined}
          style={clickawayContainerStyle}
          ref={tappableRef}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              pointerEvents: editingText ? 'all' : 'none',
              overflow: 'hidden',
              margin: '0px',
              padding: '0px',
              border: '0px',
              font: 'inherit',
              verticalAlign: 'baseline',
            }}
          >
            <div>
              {editingText ? (
                <ContentEditable
                  innerRef={autoFocusRef}
                  style={editingStyle}
                  html={props.data.text}
                  onChange={onContentChange}
                  tagName="div"
                  onBlur={onCancelEdit}
                />
              ) : (
                <ContentEditable
                  disabled
                  style={viewingStyle}
                  html={
                    sanitizedHtml && sanitizedHtml.length
                      ? sanitizedHtml
                      : 'Enter Text'
                  }
                  tagName="div"
                  onChange={voidfn}
                />
              )}
            </div>
          </div>
        </div>
      </ScaleWithPageContainer>
    );
  }
);
